import React, { useContext, useMemo, useEffect } from 'react';
import { Grid, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import UserSearch from '@/components/_common/AssigneeSelect/UserSearch';
import BillableCustomerSearch from '@/components/_common/BillableCustomerSearch';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectDraftState } from '@/redux/selectors/orderPlacementSelectors';
import { useAppSelector } from '@/redux/hooks';
import { selectOrderCategories } from '@/redux/selectors/constantsSelectors';
import Autocomplete from '@/components/_common/Form/Autocomplete';
import { FormValues } from '@/redux/slices/slice_types';
import OPAdditionalOrderFields from './OPAdditionalOrderFields';

const OPOrderDetails = () => {
  const { values, setFieldValue, touched, handleChange, errors } = useFormikContext<FormValues>();
  const disableBillableCustomer = useContext(ConfigContext)?.orderPlacementConfig?.disableBillableCustomer;
  const draftState = useAppSelector(selectDraftState);
  const categories = Object.entries(useAppSelector(selectOrderCategories))
    .map(([key, value]) => ({ value: key, label: value }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const customFields = useContext(ConfigContext)?.orderPlacementConfig?.addlFields?.orderDetails || {};

  // if the custom field has an ID, it's a field that is overriding an existing one
  const addlFields = useMemo(() => Object.keys(customFields).filter((key) => !customFields[key].id), [customFields]);

  const setFieldsWithDraftState = () => {
    if (!draftState?.values) return;

    if ((values.description === null || values.description === undefined) && draftState.values.description) {
      setFieldValue('description', draftState.values.description);
    }
    if ((values.po_number === null || values.po_number === undefined) && draftState.values.po_number) {
      setFieldValue('po_number', draftState.values.po_number);
    }
    if ((values.managed_by === null || values.managed_by === undefined) && draftState.values.managed_by) {
      setFieldValue('managed_by', draftState.values.managed_by);
    }
    if ((values.billable_customer === null || values.billable_customer === undefined) && draftState.values.billable_customer) {
      setFieldValue('billable_customer', draftState.values.billable_customer);
    }
    addlFields.forEach((fieldKey) => {
      if ((values?.[fieldKey] === null || values?.[fieldKey] === undefined) && draftState.values?.[fieldKey]) {
        setFieldValue(fieldKey, draftState.values[fieldKey]);
      }
    });
    if (categories && (values.category === null || values.category === undefined) && draftState.values.category) {
      setFieldValue('category', draftState.values.category);
    }
  };

  useEffect(() => {
    if (draftState?.values) {
      setFieldsWithDraftState();
    }
  }, [draftState?.draftId]);

  return (
    <Grid container spacing={5}>
      <Grid item xs={12} sm={6} md={5} lg={3}>
        <TextField
          fullWidth
          key="description"
          name="description"
          label={customFields?.description?.label || 'Description'}
          value={values.description || ''}
          onChange={handleChange}
          variant="filled"
          required
          error={touched.description && Boolean(errors.description)}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={5} lg={3}>
        <TextField
          fullWidth
          key="po_number"
          name="po_number"
          label={customFields?.po_number?.label || 'PO Number'}
          variant="filled"
          value={values.po_number || ''}
          onChange={handleChange}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={5} lg={3}>
        <UserSearch
          hideUnassigned
          fullWidth
          size="medium"
          key="managed_by"
          placeholder="Start typing to find a user."
          value={values.managed_by}
          onSelect={(e, val) => setFieldValue('managed_by', val)}
          isActive
        />
      </Grid>
      {!disableBillableCustomer && (
        <Grid item xs={12} sm={6} md={5} lg={3}>
          <BillableCustomerSearch
            key="billable_customer"
            onSelect={(e, val) => setFieldValue('billable_customer', val)}
            label={customFields?.billable_customer?.label || 'Billable Customer'}
            value={values.billable_customer}
            skipDispatch
          />
        </Grid>
      )}
      {!!categories.length && (
        <Grid item xs={12} sm={6} md={5} lg={3}>
          <Autocomplete
            label="Category"
            key="category"
            options={categories}
            value={categories.find((c) => c.value === values.category)?.label || null}
            isOptionEqualToValue={(option, value) => option.label === value}
            placeholder="Start typing to search"
            onChange={(e, val) => setFieldValue('category', val.value)}
            noOptionsText="No Categories Found"
            error={touched.category && Boolean(errors.category)}
          />
        </Grid>
      )}
      <OPAdditionalOrderFields addlFields={addlFields} customFields={customFields} />
    </Grid>
  );
};

export default OPOrderDetails;
